import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Clock, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../api';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/apiError';

export default function RegisterPage() {
  const [form, setForm] = useState({ email: '', password: '', company_name: '', buyer_name: '', phone: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await authAPI.register(form);
      if (res.data.pending_approval) {
        setPendingApproval(true);
      } else {
        toast.success('Account created!');
        navigate('/');
      }
    } catch (err) {
      toast.error(formatApiError(err, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  if (pendingApproval) {
    return (
      <div className="pw-container py-16">
        <div className="max-w-md mx-auto text-center">
          <div className="w-16 h-16 bg-[#e7f1ea] rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock size={28} className="text-[#2f6b4f]" />
          </div>
          <h1 data-testid="pending-approval-title" className="text-xl font-semibold tracking-wider uppercase mb-4">Account Pending Approval</h1>
          <p className="text-sm text-[#6f6a63] mb-3 leading-relaxed">
            Thank you for registering! Your account has been created and is now
            <span className="font-medium text-[#b28a4a]"> pending approval</span> by the Petals West team.
          </p>
          <p className="text-sm text-[#6f6a63] mb-8 leading-relaxed">
            We will review your information and notify you once your account has been approved. This process typically takes 1-2 business days.
          </p>
          <div className="bg-[#fbfaf8] border border-[#e7e3dd] rounded-xl p-5 text-left mb-6">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#2d2d2d] mb-3">What happens next?</h3>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-xs text-[#6f6a63]"><CheckCircle2 size={14} className="text-[#2f6b4f] mt-0.5 flex-shrink-0" />Our team reviews your company information</li>
              <li className="flex items-start gap-2 text-xs text-[#6f6a63]"><CheckCircle2 size={14} className="text-[#2f6b4f] mt-0.5 flex-shrink-0" />Your account gets approved and activated</li>
              <li className="flex items-start gap-2 text-xs text-[#6f6a63]"><CheckCircle2 size={14} className="text-[#2f6b4f] mt-0.5 flex-shrink-0" />You can log in to see prices and place orders</li>
            </ul>
          </div>
          <Link to="/" className="inline-block bg-[#2f3e36] text-white px-8 py-3 rounded-lg text-sm tracking-wider uppercase font-medium hover:bg-[#253029] transition-colors">
            Back to Homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pw-container py-12">
      <div className="max-w-md mx-auto">
        <h1 className="text-xl font-semibold tracking-wider uppercase mb-6 text-center">Create Account</h1>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="register-company" className="text-xs text-[#6f6a63] block mb-1">Company Name</label>
            <input id="register-company" type="text" autoComplete="organization" maxLength={200} value={form.company_name} onChange={(e) => setForm(f => ({ ...f, company_name: e.target.value }))} className="w-full px-4 py-3 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
          </div>
          <div>
            <label htmlFor="register-name" className="text-xs text-[#6f6a63] block mb-1">Buyer Name</label>
            <input id="register-name" type="text" autoComplete="name" maxLength={200} value={form.buyer_name} onChange={(e) => setForm(f => ({ ...f, buyer_name: e.target.value }))} className="w-full px-4 py-3 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
          </div>
          <div>
            <label htmlFor="register-email" className="text-xs text-[#6f6a63] block mb-1">Email *</label>
            <input id="register-email" data-testid="register-email" type="email" autoComplete="email" required value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} className="w-full px-4 py-3 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
          </div>
          <div>
            <label htmlFor="register-phone" className="text-xs text-[#6f6a63] block mb-1">Phone</label>
            <input id="register-phone" type="tel" autoComplete="tel" maxLength={50} value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} className="w-full px-4 py-3 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
          </div>
          <div>
            <label htmlFor="register-password" className="text-xs text-[#6f6a63] block mb-1">Password *</label>
            <div className="relative">
              <input id="register-password" data-testid="register-password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required minLength={12} maxLength={72} value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} aria-describedby="register-password-help" className="w-full px-4 py-3 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f] pr-11" />
              <button type="button" data-testid="register-toggle-password" onClick={() => setShowPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6f6a63] hover:text-[#2d2d2d] transition-colors p-0.5" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p id="register-password-help" className="mt-1 text-xs text-[#6f6a63]">Use at least 12 characters.</p>
          </div>
          <button data-testid="register-submit" type="submit" disabled={loading} className="w-full bg-[#2d2d2d] text-white py-3 rounded-lg text-sm tracking-wider uppercase font-medium hover:bg-[#232323] disabled:opacity-50">
            {loading ? 'Creating...' : 'Create Account'}
          </button>
        </form>
        <p className="text-center text-sm text-[#6f6a63] mt-6">Already have an account? <Link to="/login" className="text-[#2f6b4f] font-medium">Sign In</Link></p>
      </div>
    </div>
  );
}
